import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogOut, MapPin, ArrowRight, BookOpenCheck, UsersRound } from "lucide-react";

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/69127f46-487f-4c9b-81b6-5c40b97c2506/images/3643e6bb7906d8242c2030ce0e68949de92202005c3e924b2eaf89fb219ddfb4.png";

function formatEUR(v) {
    if (v == null) return null;
    if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mio. €`;
    if (v >= 10_000) return `${(v / 1000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} Tsd. €`;
    return `${v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export default function PublisherSelection() {
    const { tenants, setActiveTenant, clearTenant } = useTenant();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [overview, setOverview] = useState({}); // tenant_id -> {zeitraum, lagerwert, bilanzwert, aktive_titel}

    useEffect(() => {
        if (user?.role !== "admin") return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/tenants/overview");
                if (cancelled) return;
                const map = {};
                (r.data || []).forEach((e) => { map[e.tenant_id] = e; });
                setOverview(map);
            } catch {
                // still, cards remain, just without KPI-Block
            }
        })();
        return () => { cancelled = true; };
    }, [user?.role]);

    const choose = (t) => {
        setActiveTenant(t);
        navigate("/", { replace: true });
    };

    const openBenutzerverwaltung = () => {
        // Kein Verlag soll gesetzt sein, wenn Admin bewusst die
        // mandantenübergreifende Benutzerverwaltung öffnet.
        clearTenant?.();
        navigate("/benutzer");
    };

    return (
        <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
            <div
                className="absolute inset-0 opacity-30 bg-cover bg-center"
                style={{ backgroundImage: `linear-gradient(180deg, rgba(26,26,46,0.5), rgba(26,26,46,0.95)), url(${HERO_BG})` }}
            />
            <div className="absolute inset-0 grid-bg opacity-[0.06]" />

            <div className="relative max-w-6xl mx-auto px-6 pt-10 pb-16">
                <div className="flex items-center justify-between mb-12">
                    <div className="flex items-center gap-3">
                        <BookOpenCheck className="h-7 w-7 text-tenant" />
                        <span className="font-heading text-xl tracking-tight">INVENTUR PRO</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name} · {user?.role}</span>
                        {user?.role === "admin" && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={openBenutzerverwaltung}
                                data-testid="admin-benutzer-from-selection"
                                className="border-border"
                            >
                                <UsersRound className="h-4 w-4 mr-1.5" /> Benutzerverwaltung
                            </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-from-selection">
                            <LogOut className="h-4 w-4 mr-1.5" /> Abmelden
                        </Button>
                    </div>
                </div>

                <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">Mandantenauswahl</p>
                <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold max-w-2xl">
                    Wählen Sie einen <span className="text-tenant">Verlag</span>, um fortzufahren.
                </h1>
                <p className="mt-3 text-muted-foreground max-w-xl">
                    Jeder Verlag hat eigene Stamm- und Bewegungsdaten. Sie können später jederzeit über die Topbar wechseln.
                </p>

                <div className="grid md:grid-cols-3 gap-6 mt-12">
                    {tenants.map((t, idx) => (
                        <Card
                            key={t.tenant_id}
                            className="group relative p-7 bg-surface1 border-border cursor-pointer overflow-hidden transition-all hover:-translate-y-1 hover:border-[color:var(--hover-c)] animate-fade-up"
                            style={{ "--hover-c": t.color, animationDelay: `${idx * 80}ms` }}
                            onClick={() => choose(t)}
                            data-testid={`tenant-card-${t.tenant_id}`}
                        >
                            <div
                                className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-20 blur-2xl"
                                style={{ background: t.color }}
                            />
                            <div
                                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold"
                                style={{ background: `${t.color}1f`, color: t.color }}
                            >
                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: t.color }} />
                                Verlag
                            </div>
                            <h3 className="font-heading text-2xl font-semibold tracking-tight mt-4">{t.name}</h3>
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1.5">
                                <MapPin className="h-3.5 w-3.5" /> {t.city}
                            </div>
                            {user?.role === "admin" && (
                                <TenantKpiBlock data={overview[t.tenant_id]} color={t.color} />
                            )}
                            <div className="mt-8 flex items-center justify-between">
                                <span className="text-xs uppercase tracking-widest text-muted-foreground">Auswählen</span>
                                <ArrowRight
                                    className="h-5 w-5 transition-transform group-hover:translate-x-1"
                                    style={{ color: t.color }}
                                />
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
}

function TenantKpiBlock({ data, color }) {
    // Wenn Endpoint noch nicht geladen hat: Skeleton
    if (data === undefined) {
        return (
            <div className="mt-5 pt-4 border-t border-border/60">
                <div className="h-3 w-24 rounded bg-surface2 animate-pulse mb-2" />
                <div className="h-5 w-32 rounded bg-surface2 animate-pulse" />
            </div>
        );
    }
    if (!data?.zeitraum) {
        return (
            <div className="mt-5 pt-4 border-t border-border/60">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground/70">Bewertung</div>
                <div className="text-sm text-muted-foreground italic mt-1">Noch keine Bewertungsdaten</div>
            </div>
        );
    }
    return (
        <div className="mt-5 pt-4 border-t border-border/60 space-y-2.5" data-testid={`tenant-kpi-${data.tenant_id}`}>
            <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest text-muted-foreground/70">Periode</span>
                <span className="text-xs font-medium">{data.zeitraum}</span>
            </div>
            <div className="flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-widest text-muted-foreground/70">Lagerwert</span>
                <span className="text-sm font-medium text-foreground/90 tabular-nums">
                    {formatEUR(data.lagerwert)}
                </span>
            </div>
            <div className="flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-widest text-muted-foreground/70">Bilanzwert</span>
                <span className="text-base font-semibold tabular-nums" style={{ color }}>
                    {formatEUR(data.bilanzwert)}
                </span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground/60">
                {data.aktive_titel?.toLocaleString("de-DE")} aktive Titel
            </div>
        </div>
    );
}
